import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const serviceOptions = [
  { value: 'コールセンター運営', label: 'コールセンター運営について' },
  { value: '業務委託', label: '業務委託について' },
  { value: 'カスタマーサポート代行', label: 'カスタマーサポート代行について' },
  { value: '料金プラン', label: '料金プランについて' },
  { value: 'その他', label: 'その他' },
];

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
};

const initialFormState: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  const toggleService = (value: string) => {
    setSelectedServices((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      alert('必須項目を入力してください。');
      return false;
    }

    if (form.message.length > 500) {
      alert('お問い合わせ内容は500文字以内でご入力ください。');
      return false;
    }

    if (!privacyChecked) {
      alert('プライバシーポリシーへの同意が必要です。');
      return false;
    }

    return true;
  };

  const handleConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);

    const data = new URLSearchParams();
    data.set('name', form.name.trim());
    data.set('company', form.company.trim());
    data.set('email', form.email.trim());
    data.set('phone', form.phone.trim());
    data.set('inquiry_type', selectedServices.join('、') || '未選択');
    data.set('message', form.message.trim());
    data.set('privacy', privacyChecked ? '同意済み' : '未同意');

    try {
      const response = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      const text = (await response.text()).trim();

      if (!response.ok || text !== 'OK') {
        throw new Error('send_failed');
      }

      setForm(initialFormState);
      setSelectedServices([]);
      setPrivacyChecked(false);
      setResult({
        status: 'success',
        message: 'お問い合わせありがとうございます。\n担当者より折り返しご連絡いたします。',
      });
    } catch {
      setResult({
        status: 'error',
        message: '送信に失敗しました。時間をおいて再度お試しください。',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="bg-white min-h-screen">
        <section className="relative bg-[#0f1628] geo-pattern pt-32 pb-20">
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
            <span className="font-cormorant text-[#c9a84c] text-sm tracking-[0.4em] uppercase block mb-3">Contact</span>
            <h1 className="font-serif-jp text-white text-4xl md:text-5xl tracking-wider font-medium mb-4">お問い合わせ</h1>
            <div className="flex items-center gap-3">
              <div className="w-12 h-px bg-[#c9a84c]"></div>
              <div className="w-1.5 h-1.5 bg-[#c9a84c] rotate-45"></div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="font-serif-jp text-[#0f1628] text-xl tracking-wider mb-6">お問い合わせ先</h2>
                <div className="flex flex-col gap-6">
                  {[
                    { icon: 'ri-map-pin-line', label: '所在地', value: '埼玉県三郷市高州３丁目１６４番地バンブーハイツ１０７' },
                    { icon: 'ri-phone-line', label: '電話番号', value: '090-6340-5373' },
                    { icon: 'ri-mail-line', label: 'メール', value: 'administrator@az-consulting-llc.com' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center border border-[#c9a84c]/40 flex-shrink-0">
                        <i className={`${item.icon} text-[#c9a84c] text-sm`}></i>
                      </div>
                      <div>
                        <span className="text-[#0f1628]/40 text-xs tracking-widest block mb-1">{item.label}</span>
                        <span className="text-[#0f1628] text-xs font-sans leading-relaxed">{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-2">
                <form onSubmit={handleConfirm} className="flex flex-col gap-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-2">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="name"
                        type="text"
                        required
                        value={form.name}
                        onChange={handleInputChange}
                        className="w-full border border-[#0f1628]/20 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#c9a84c] transition-colors bg-white"
                        placeholder="山田 太郎"
                      />
                    </div>
                    <div>
                      <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-2">会社名</label>
                      <input
                        name="company"
                        type="text"
                        value={form.company}
                        onChange={handleInputChange}
                        className="w-full border border-[#0f1628]/20 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#c9a84c] transition-colors bg-white"
                        placeholder="株式会社〇〇"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleInputChange}
                      className="w-full border border-[#0f1628]/20 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#c9a84c] transition-colors bg-white"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-2">電話番号</label>
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleInputChange}
                      className="w-full border border-[#0f1628]/20 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#c9a84c] transition-colors bg-white"
                      placeholder="090-0000-0000"
                    />
                  </div>

                  <div>
                    <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-3">
                      お問い合わせ種別 <span className="text-[#0f1628]/30 text-xs">（複数選択可）</span>
                    </label>
                    <div className="flex flex-col gap-2">
                      {serviceOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleService(opt.value)}
                          className="flex items-center gap-3 text-left"
                        >
                          <div
                            className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                              selectedServices.includes(opt.value)
                                ? 'bg-[#c9a84c] border-[#c9a84c]'
                                : 'border-[#0f1628]/30'
                            }`}
                          >
                            {selectedServices.includes(opt.value) && (
                              <i className="ri-check-line text-white text-xs"></i>
                            )}
                          </div>
                          <span
                            className={`text-sm font-sans transition-colors duration-200 ${
                              selectedServices.includes(opt.value) ? 'text-[#0f1628]' : 'text-[#0f1628]/60'
                            }`}
                          >
                            {opt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[#0f1628]/50 text-xs tracking-widest block mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      value={form.message}
                      onChange={handleInputChange}
                      className="w-full border border-[#0f1628]/20 px-4 py-3 text-sm font-sans focus:outline-none focus:border-[#c9a84c] transition-colors bg-white resize-none"
                      placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                    ></textarea>
                    <p className="text-[#0f1628]/40 text-xs font-sans mt-2 text-right">{form.message.length}/500</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyChecked}
                      onChange={(event) => setPrivacyChecked(event.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded-none border-[#0f1628]/30 text-[#c9a84c] focus:ring-[#c9a84c]"
                    />
                    <span className="text-sm font-sans text-[#0f1628]/60 leading-relaxed">
                      <Link to="/privacy" className="text-[#c9a84c] hover:underline cursor-pointer">
                        プライバシーポリシー
                      </Link>
                      に同意する <span className="text-red-500">*</span>
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0f1628] text-white py-4 text-sm tracking-widest font-sans hover:bg-[#1a2540] transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    入力内容を確認する
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] bg-[#0f1628]/80 backdrop-blur-sm flex items-center justify-center p-5 md:p-8 overflow-y-auto">
          <div
            className="mx-auto max-h-[88vh] overflow-y-auto rounded-[28px] bg-white border border-[#c9a84c]/30 p-7 md:p-10 shadow-2xl"
            style={{ width: 'calc(100vw - 3rem)', maxWidth: '38rem', boxSizing: 'border-box' }}
          >
            <div className="flex items-start justify-between gap-4 mb-10">
              <div className="pr-4 md:pr-8">
                <span className="font-cormorant text-[#c9a84c] text-sm tracking-[0.4em] uppercase block mb-2">Confirm</span>
                <h2 className="font-serif-jp text-[#0f1628] text-2xl tracking-wider font-medium">入力内容の確認</h2>
                <p className="text-sm text-[#0f1628]/45 font-sans mt-3 leading-relaxed">
                  送信前に内容をご確認ください。修正が必要な場合は下のボタンから戻れます。
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="w-10 h-10 rounded-full border border-[#0f1628]/10 text-[#0f1628]/60 hover:text-[#0f1628] hover:border-[#c9a84c]/50 transition-colors flex items-center justify-center"
                aria-label="閉じる"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4 text-sm">
              {[
                { label: 'お名前', value: form.name },
                { label: '会社名', value: form.company || '未入力' },
                { label: 'メールアドレス', value: form.email },
                { label: '電話番号', value: form.phone || '未入力' },
                { label: 'お問い合わせ種別', value: selectedServices.join('、') || '未選択' },
                { label: 'お問い合わせ内容', value: form.message },
              ].map((item) => (
                <div key={item.label} className="border border-[#0f1628]/10 rounded-2xl bg-[#fcfbf8] px-4 py-4 md:px-5">
                  <p className="text-[#0f1628]/40 text-xs tracking-[0.2em] font-sans mb-2">{item.label}</p>
                  <p className="text-[#0f1628] text-sm font-sans whitespace-pre-line break-words">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-full border border-[#0f1628]/20 text-[#0f1628] py-3.5 text-sm tracking-widest font-sans hover:bg-[#f8f5ef] transition-colors"
              >
                戻って修正する
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex-1 rounded-full bg-[#0f1628] text-white py-3.5 text-sm tracking-widest font-sans hover:bg-[#1a2540] transition-colors"
              >
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="fixed inset-0 z-[80] bg-[#0f1628]/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div
            className="mx-auto bg-white border border-[#c9a84c]/30 p-8 text-center"
            style={{ width: 'calc(100vw - 3rem)', maxWidth: '28rem', boxSizing: 'border-box' }}
          >
            <div className="w-14 h-14 border border-[#c9a84c]/40 flex items-center justify-center mx-auto mb-5">
              <i className="ri-loader-4-line animate-spin text-2xl text-[#c9a84c]"></i>
            </div>
            <h2 className="font-serif-jp text-[#0f1628] text-xl tracking-wider mb-2">送信中です</h2>
            <p className="text-sm text-[#0f1628]/50 font-sans">内容を送信しています。しばらくお待ちください。</p>
          </div>
        </div>
      )}

      {result && (
        <div className="fixed inset-0 z-[90] bg-[#0f1628]/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div
            className="mx-auto bg-white border border-[#c9a84c]/30 p-8 text-center"
            style={{ width: 'calc(100vw - 3rem)', maxWidth: '28rem', boxSizing: 'border-box' }}
          >
            <div className="w-14 h-14 border border-[#c9a84c]/40 flex items-center justify-center mx-auto mb-5">
              <i className={`text-2xl ${result.status === 'success' ? 'ri-check-line text-[#c9a84c]' : 'ri-close-line text-red-500'}`}></i>
            </div>
            <h2 className="font-serif-jp text-[#0f1628] text-xl tracking-wider mb-2">
              {result.status === 'success' ? '送信完了' : '送信失敗'}
            </h2>
            <p className="text-sm text-[#0f1628]/50 font-sans whitespace-pre-line mb-6">{result.message}</p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="w-full bg-[#0f1628] text-white py-3 text-sm tracking-widest font-sans hover:bg-[#1a2540] transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}
